// @need-translate
import router from 'next/router';
import {ActionFunction, assign, EventObject, NonReducibleUnknown} from 'xstate';
import {getCurrentUrlWithAdditonalParam} from '../../../../common/getCurrentUrlWithAdditonalParam';
import {TRIGGER_COMMAND_KEY} from '../../../../common/shared/constants';
import {
  COMMAND_KEY,
  saveCommand,
} from '../../../../common/shared/hooks/useCommandWithLocalStorage';
import {removeSimpleDialog} from '../../../../dialog/simpleDialogHandler';
import {getSerializedPagewebError} from '../../../../errors/getSerializedPagewebError.function';
import {PagewebErrorType} from '../../../../errors/PagewebErrorType.enum';
import {QueryFromPage, TicketInfo} from '../../../../graphql/generated/types';
import {handleLoginPageweb} from '../../../../user/handleLoginPageweb.function';
import {setTicketFlowInProgress} from '../../../content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import {initialContext, QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE} from '../constant';
import {
  BuyTicketFlowActions,
  BuyTicketFlowContext,
  BuyTicketFlowEvents,
} from '../type';

export const buyTicketFlowActions: {
  [x in keyof BuyTicketFlowActions]: ActionFunction<
    BuyTicketFlowContext,
    BuyTicketFlowEvents,
    BuyTicketFlowEvents,
    NonReducibleUnknown,
    any,
    any,
    any,
    any,
    EventObject
  >;
} = {
  login: ({context}) => {
    if (context.flowType === 'TICKET_PROCESS') {
      if (!context.ticketProcessParam) {
        handleLoginPageweb();
        return;
      }
      const {ticketProcessParam, currentProductId} = context;
      saveCommand({
        commandKey: COMMAND_KEY.CLICK_BUY_TICKETS,
        command: {
          ...ticketProcessParam,
          expiredConfirmed: context.expiredConfirmed,
          location: window.location.pathname,
          currentProductId,
        },
      });
    } else if (context.flowType === 'BUY_SET_PROCESS') {
      if (!context.buySetProcessParam) {
        handleLoginPageweb();
        return;
      }
      const {buySetProcessParam} = context;
      saveCommand({
        commandKey: COMMAND_KEY.CLICK_SET_BUY,
        command: {
          ...buySetProcessParam,
          location: window.location.pathname,
        },
      });
    }
    handleLoginPageweb({
      callbackUrl: getCurrentUrlWithAdditonalParam(
        TRIGGER_COMMAND_KEY,
        context.flowType === 'TICKET_PROCESS'
          ? COMMAND_KEY.CLICK_BUY_TICKETS
          : COMMAND_KEY.CLICK_SET_BUY
      ),
    });
  },
  clearContext: assign(initialContext),
  initializeTicketProcessContext: assign(({event}) => ({
    flowType: 'TICKET_PROCESS',
    isMobile: event.isMobile,
    user: event.user,
    ticketProcessParam: event.ticketProcessParam,
    queryClient: event.queryClient,
    dispatch: event.dispatch,
    storedTicketInfo: event.storedTicketInfo,
    currentProductId: event.currentProductId,
    expiredConfirmed: event.expiredConfirmed,
    selectedNonstopWatchingBuyTicketInfo:
      event.selectedNonstopWatchingBuyTicketInfo,
    onInit: event.onInit,
    onSuccess: event.onSuccess,
    onError: event.onError,
    needProgressDelay: false,
    isShortsDrama: event.isShortsDrama,
  })),
  initializeBuySetProcessContext: assign(({event}) => ({
    flowType: 'BUY_SET_PROCESS',
    isMobile: event.isMobile,
    user: event.user,
    buySetProcessParam: event.buySetProcessParam,
    queryClient: event.queryClient,
    storedTicketInfo: event.storedTicketInfo,
    dispatch: event.dispatch,
    onInit: event.onInit,
    onSuccess: event.onSuccess,
    onError: event.onError,
    needProgressDelay: false,
  })),
  setSeriesSingleInfo: assign(({event}) => ({
    simpleSeriesSingleInfo: event.output,
  })),
  setReadyToUseTicket: assign(({event}) => ({
    readyToUseTicket: event.output,
  })),
  setSetBuyTicketInfo: assign(({event}) => ({
    setBuyTicketInfo: event.output,
  })),
  setSetReadyToUseTicket: assign(({event}) => ({
    setReadyToUseTicket: event.output,
  })),
  setSetBuyResult: assign(({event}) => ({setBuyResult: event.output})),
  setError: assign(({event}) => {
    // sanity check
    if (!(event.error instanceof Error) && typeof event.error !== 'string') {
      return;
    }
    const toSaveError = event.error
      ? getSerializedPagewebError(event.error)
      : undefined;

    return {
      error: toSaveError,
    };
  }),
  clearError: assign({error: undefined}),
  setAgeNotGrantedError: assign({
    error: ({context}) => ({
      isSerializedPagewebError: true,
      type: PagewebErrorType.AgeNotGranted,
      message: '연령 확인이 필요 합니다',
      extra: {
        seriesId: String(context.ticketProcessParam?.seriesId),
        contentAgeGrade: String(context.simpleSeriesSingleInfo?.singleAgeGrade),
        userAge: String(context.user?.age),
        userAuthenticatedAt: String(context.user?.authenticatedAt),
      },
    }),
  }),
  setWebNotAccessibleError: assign({
    error: ({context}) => ({
      isSerializedPagewebError: true,
      type: PagewebErrorType.WebNotAccessibleError,
      message: '웹에서 감상 불가',
      extra: {
        seriesId: String(context.ticketProcessParam?.seriesId),
        productId: String(context.ticketProcessParam?.productId),
        contentAgeGrade: String(context.simpleSeriesSingleInfo?.singleAgeGrade),
      },
    }),
  }),
  setselectedNonstopWatchingBuyTicketInfo: assign(({event}) => ({
    selectedNonstopWatchingBuyTicketInfo: event.output,
  })),
  setExpiredConfirmed: assign({expiredConfirmed: true}),
  startNextTicketProcess: assign(({context}) => {
    if (
      !context.ticketProcessParam ||
      !context.readyToUseTicket ||
      !context.readyToUseTicket.nextItem
    ) {
      return context;
    }

    return {
      ...context,
      ticketProcessParam: {
        ...context.ticketProcessParam,
        pickExactly: true,
        productId: context.readyToUseTicket.nextItem.productId,
        ageGrade: context.readyToUseTicket.nextItem.ageGrade,
        isFree: context.readyToUseTicket.nextItem.isFree,
        slideType: context.readyToUseTicket.nextItem.slideType,
      },
    };
  }),
  setUseTicketType: assign(({event}) => ({
    useTicketType: event.output.ticketType,
  })),
  setSetUseTicketByPriority: assign(({context}) => ({
    useTicketType:
      context.readyToUseTicket?.available?.ticketOwnType ||
      context.readyToUseTicket?.available?.ticketRentalType,
  })),
  setUseTicketTypeOwnType: assign(({context}) => ({
    useTicketType: context.readyToUseTicket?.available?.ticketOwnType,
  })),
  setUseTicketTypeRentalType: assign(({context}) => ({
    useTicketType: context.readyToUseTicket?.available?.ticketRentalType,
  })),
  setBuyAndUseTicketInfoFromStoredTicketInfo: assign(({context}) => ({
    buyAndUseTicketInfo: context.storedTicketInfo,
  })),
  setSetBuyTicketInfoFromStoredTicketInfo: assign(({context}) => ({
    setBuyTicketInfo: context.storedTicketInfo,
  })),
  setBuyAndUseTicketInfo: assign(({event}) => ({
    buyAndUseTicketInfo: event.output.ticketInfo,
  })),
  setNonstopWatchingBuyAndUseTicketInfo: assign(({context}) => {
    const {
      readyToUseTicket,
      selectedNonstopWatchingBuyTicketInfo: selectedTicketInfo,
    } = context;
    if (!selectedTicketInfo || !readyToUseTicket?.purchase) {
      return;
    }

    // 이용권타입만 있을 땐 작품의 모든 이용권 ticket_id 가 동일했으나 캐시타입 이용권이 들어오면서 ticket_id 가 달라질 수 있게 되었습니다. ready_to_use 의 결과와 정주행모드의 ticket_id 가 동일하면 그걸 우선하고, 없으면 같은 ticketType 을 꺼내 씁니다(ios 동일)
    const purchasable = readyToUseTicket.purchase;

    const toBuyTicketInfo: TicketInfo | undefined = [
      purchasable.ticketOwn?.ticketId,
      purchasable.ticketRental?.ticketId,
    ].includes(selectedTicketInfo.ticketId)
      ? selectedTicketInfo
      : purchasable.ticketRental?.ticketType === selectedTicketInfo.ticketType
        ? purchasable.ticketRental
        : purchasable.ticketOwn?.ticketType === selectedTicketInfo.ticketType
          ? purchasable.ticketOwn
          : undefined;

    if (!toBuyTicketInfo) {
      throw new Error('Invalid ticket info');
    }

    return {
      buyAndUseTicketInfo: toBuyTicketInfo,
    };
  }),
  goToBuyTicket: ({context}) => {
    if (!context.ticketProcessParam) {
      return;
    }

    const destination = `/buy/ticket?seriesId=${context.ticketProcessParam.seriesId}&productId=${
      context.ticketProcessParam.productId
    }${context.currentProductId ? `&from=${context.currentProductId}` : ''}${
      context.ticketProcessParam.productId &&
      context.readyToUseTicket?.series?.isWaitfree &&
      context.readyToUseTicket.single?.waitfreeBlock
        ? '&waitfreeBlock=true'
        : ''
    }`;

    // 뷰어로 진입할 때 이용 권한이 없어서 이용권 충전 화면으로 이동하는 경우 뒤로가기 했을 때 뷰어 진입 전 페이지로 이동하기 위해 replace 합니다.
    if (context.ticketProcessParam.queryFrom === QueryFromPage.ViewerEntry) {
      router.replace(destination);
    } else {
      router.push(destination);
    }
  },
  goToViewer: ({context}) => {
    if (!context.ticketProcessParam) {
      return;
    }
    assign({needProgressDelay: true});
    router.push(
      `/content/${context.ticketProcessParam.seriesId}/viewer/${context.ticketProcessParam.productId}`
    );
  },
  goToViewerAfterSetBuy: ({context}) => {
    if (
      !context.buySetProcessParam?.seriesId ||
      !context.buySetProcessParam?.productId
    ) {
      return;
    }
    assign({needProgressDelay: true});
    router.push(
      `/content/${context.buySetProcessParam.seriesId}/viewer/${
        context.buySetProcessParam.productId
      }${
        context.buySetProcessParam.ticketType
          ? `?${QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE}=${context.buySetProcessParam.ticketType}`
          : ''
      }`
    );
  },
  goToSeriesHome: ({context}) => {
    if (!context.buySetProcessParam) {
      return;
    }
    router.push(`/content/${context.buySetProcessParam.seriesId}`);
  },
  goToSetHome: ({event, context}) => {
    if (!event.output) {
      return;
    }
    const seriesId = context.ticketProcessParam?.seriesId;
    const productId = context.ticketProcessParam?.productId;
    router.push(
      `/content/set-overview/${event.output.setId}${
        seriesId && productId
          ? `?seriesId=${seriesId}&productId=${productId}`
          : ''
      }`
    );
  },
  setLoginPopupConfirm: assign({loginPopupConfirm: true}),
  callOnInit: ({context}) => {
    context.onInit?.();
  },
  callOnSuccess: ({context}) => {
    // 이용권 플로우가 성공했다면 이후 플로우에 영향을 미치지 않게 쿼리 파라미터를 제거합니다.
    if (router.query[QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE]) {
      delete router.query[QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE];
      router.replace(
        {
          pathname: router.pathname,
          query: router.query,
        },
        undefined,
        {
          shallow: true,
        }
      );
    }

    context.onSuccess?.(context);
  },
  callOnError: ({context}) => {
    context.onError?.(context.error);
  },
  callOnFlowComplete: ({context}) => {
    if (!context.error) {
      removeSimpleDialog();
    }
    handleRouteChangeComplete(context);
  },
};

const handleRouteChangeComplete = (context: BuyTicketFlowContext) => {
  const handler = () => {
    context.dispatch && context.dispatch(setTicketFlowInProgress(false));
    router.events.off('routeChangeComplete', handler);
  };

  if (context.needProgressDelay) {
    router.events.on('routeChangeComplete', handler);
  } else {
    context.dispatch && context.dispatch(setTicketFlowInProgress(false));
  }
};
